import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import _keys from 'lodash/keys';
import _pickBy from 'lodash/pickBy';
import {isSearchSaved} from '../../selectors/savedSearches';
import {addSavedSearch, showModal} from '../../actions';
import {dataLayerPush, getPageData, getPriceRange, nameFormat, getStatus, getPropertyTypeFromFilter} from '../../lib/tracking';
import {sortTypes, saleDateOptions, popularFeaturesForSearch} from '../../lib/config';
import {minMaxText, amountFormatting} from '../../lib/formatting';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import LoginModal from '../Modal/LoginModal/LoginModal';
import NewFeatureIndicator from '../NewFeatureIndicator/NewFeatureIndicator';
import styles from './SavedSearch.scss';
import attributeStyles from '../PropertyAttribute/PropertyAttribute.scss';

let moreLabels = {
  isEstablishedHome: 'Established Home',
  isHomeLandPackage: 'Home/Land Package',
  isNewBuild: 'New Builds',
  isNewConstruction: 'New Construction',
  dateAvailableFrom: 'Available from date',
  excludeUnderOffer: 'Exclude Under Offer',
  includeSurroundSuburbs: 'Include Surrounding Areas',
  rentedHistory: 'Rental History',
  soldHistory: 'Sold History',
}
let filterLabels = {...popularFeaturesForSearch, ...moreLabels};

const searchType = (propertyStatus) => {
  if (Object.values(propertyStatus).every(Boolean)) return 'All';
  return Object.keys(propertyStatus).filter(item => propertyStatus[item] === true).map(item => nameFormat(item)).join(', ').replace('-', ' ');
}

const propertyType = (type) => {
  if (Object.values(type).every(item => !item)) return 'All types';
  let pt = Object.keys(type).filter(item => type[item] === true);
  return `${pt.toSpliced(3).join(', ')}${pt.length > 3 ? '...' : ''}`;
}

const priceRange = (range) => {
  if (!range.min && !range.max) return 'Any price';
  return minMaxText(amountFormatting(range.min), amountFormatting(range.max));
}

const attributeLabel = (range) => {
  if (!range.min && !range.max) return 'Any';
  return minMaxText(amountFormatting(range.min), amountFormatting(range.max)).replace(/\$/igm, '').replace(/\$/igm, '');
}

const delimitedFilters = (filters) => {
  let filterStrings = [];
  filterStrings.push(searchType(filters.propertyStatus));
  filterStrings.push(`${nameFormat(filters.address.suburb)} ${filters.address?.stateCode?.toUpperCase()} ${filters.address.postCode} ${filters.includeSurroundSuburbs ? '(and surrounding areas)' : ''}`);
  filterStrings.push(propertyType(filters.propertyType));
  filterStrings.push(priceRange(filters.price));
  return filterStrings.join(' | ')
}

const moreFilters = (filters) => {
  if (filters) {
    let moreString = undefined;
    let mf = ['dateAvailableFrom', 'excludeUnderOffer', 'rentedHistory', 'soldHistory'].filter(item => filters[item]);
    mf.push(...Object.keys(filters.additionalFeaturesQuery).filter(item => filters.additionalFeaturesQuery[item] === true));
    if (mf.length) moreString = `${filterLabels[mf[0]]}${mf.length > 1 ? `, +${mf.length-1} more` : ''}`;
    else if (filters?.sort) moreString = `Sort by ${sortTypes[filters.sort].label}`;
    if (moreString) {
      return <Typography variant="body2" component="div" className="mt-3 mb-3">More Filters: {moreString}</Typography>
    }
  }
  return null;
}

const getTooltipTitle = (searchType) => {
  switch (searchType?.toLowerCase()) {
    case 'all':
    case 'for sale':
    case 'for rent':
      return 'Get alerts when new properties hit the market';
    case 'sold':
      return 'Get alerts when properties sell';
    default:
      return 'Get alerts for your saved searches';
  }
};

function SavedSearchButton(props) {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const searchSaved = useSelector(state => isSearchSaved(state));
  const filterSearch = useSelector(state => state.filterSearch);
  const [isAuthUser, setIsAuthUser] = useState(false);
  const [isDialogOpen, setDialogIsOpen] = useState(false);
  const [searchName, setSearchName] = useState(null);
  const [nameError, setNameError] = useState(false);
  const [alertFreq, setAlertFreq] = useState('Daily');
  const {searchParams} = props;

  useEffect(() => {
    if (user.authenticated !== isAuthUser) setIsAuthUser(user.authenticated);
  }, [user]);

  useEffect(() => {
    if (searchName === null && filterSearch?.address?.suburb) setSearchName(`${nameFormat(filterSearch.address.suburb)} ${filterSearch.address.stateCode.toUpperCase()} ${filterSearch.address.postCode}`);
  }, [filterSearch]);

  const saveSearch = (event) => {
    event.preventDefault();

    let updatedQuery = _cloneDeep(searchParams.query);

    if(updatedQuery.queries.length === 1
        && updatedQuery.queries[0].category ===   'RecentlySold'
        && updatedQuery.queries[0].soldDateStart) {
      updatedQuery.queries[0] = {
        ...updatedQuery.queries[0],
        filteredMonths: saleDateOptions.find(dateOption => dateOption.label === decodeURI(updatedQuery.queries[0].soldDateStart)).value
      };
      delete updatedQuery.queries[0].soldDateStart;
    }

    if (!searchSaved) {
      let searchQuery = {
        name: searchName,
        query: updatedQuery,
        sort: searchParams.sort,
        alertFrequency: !['All', 'Off Market'].includes(searchType(filterSearch?.propertyStatus)) ? alertFreq : 'Never',
      };
      dispatch(addSavedSearch(searchQuery));

      // track the save
      let pd = getPageData();
      if (pd && pd.name != 'unknown') {
        // not tracking ssr searches as it's not ui actioned
        let save_search = {
          event: 'save_search',
          suburb_name: nameFormat(filterSearch.address.suburb),
          postcode: filterSearch.address.postCode,
          state: filterSearch.address.stateCode.toUpperCase(),
          price_range: getPriceRange(filterSearch.price.min, filterSearch.price.max),
          page_type: pd.type,
          page_name: pd.name
        };
        save_search.property_status = getStatus(_keys(_pickBy(filterSearch.propertyStatus)));
        save_search.property_type = getPropertyTypeFromFilter(filterSearch.propertyType).substring(0, 41);
        ['min_bed', 'max_bed', 'min_bath', 'max_bath', 'min_car', 'max_car'].map((item) => {
          save_search[item] = 'not available';
        });
        if (filterSearch.bedroom.min) save_search.min_bed = filterSearch.bedroom.min;
        if (filterSearch.bedroom.max) save_search.max_bed = filterSearch.bedroom.max;
        if (filterSearch.bathroom.min) save_search.min_bath = filterSearch.bathroom.min;
        if (filterSearch.bathroom.max) save_search.max_bath = filterSearch.bathroom.max;
        if (filterSearch.carPark.min) save_search.min_car = filterSearch.carPark.min;
        if (filterSearch.carPark.max) save_search.max_car = filterSearch.carPark.max;
        if (searchQuery.alertFrequency) save_search.email_frequency = searchQuery.alertFrequency;
        dataLayerPush(save_search);
      }
    }
    if (isDialogOpen) setDialogIsOpen(false);
  }

  const openDialog = () => {
    setDialogIsOpen(true);
    if (!isAuthUser) {
      dispatch(showModal({
        open: true,
        title: 'Sign in to your account',
        component: <LoginModal />
      }));
    }
  }
  const closeDialog = () => {setDialogIsOpen(false);}
  const handleNameChange = (event) => setSearchName(event.target.value);
  const handleNameBlur = (event) => setNameError(event.target.value === '');
  const handleFreqChange = (event) => setAlertFreq(event.target.value);
  const isAlertsDisabled = ['All', 'Off Market'].includes(searchType(filterSearch?.propertyStatus));

  return (
    <div className="position-relative">
      {searchSaved ?
        <Button color="primary" href="/members/searches" target="_blank" rel="nofollow noopener noreferrer" className={`${styles.saveSearch_button} ${styles.saved}`}><span className={styles.editIcon}></span>Saved Search</Button>
      :
        <Button color="secondary" variant="outlined" onClick={openDialog} className={styles.saveSearch_button}><span className={styles.plusIcon}></span>Save Search</Button>
      }
      <Dialog open={(isAuthUser && isDialogOpen)} onClose={closeDialog} aria-labelledby="form-dialog-alerts" className={styles.saveDialog}>
        <DialogTitle id="form-dialog-alerts" disableTypography={true}>
          <Typography variant="h4" className="pr-5">Save Your Search &amp; Get Alerts</Typography>
          <IconButton aria-label="close" className={'position-absolute ' + styles.closeButton} onClick={closeDialog}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div>
            <TextField
              size="small"
              fullWidth
              label="Name your search"
              variant="outlined"
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              value={searchName}
              error={nameError}
              helperText={nameError ? 'Please provide a name for the search' : ''}
            />
          </div>
          <Typography variant="body1" component="div" className="mt-3 mb-3">
            {delimitedFilters(filterSearch)}
          </Typography>
          <Typography variant="body1" component="div" className="mt-3 mb-3 d-flex justify-content-start align-items-center">
            <div className={`${styles.attributeIcon} ${attributeStyles.beds} mr-1`}/><div className="mr-3">{attributeLabel(filterSearch.bedroom)}</div>
            <div className={`${styles.attributeIcon} ${attributeStyles.baths} mr-1`}/><div className="mr-3">{attributeLabel(filterSearch.bathroom)}</div>
            <div className={`${styles.attributeIcon} ${attributeStyles.carSpaces} mr-1`}/><div>{attributeLabel(filterSearch.carPark)}</div>
          </Typography>
          {moreFilters(filterSearch)}
          <div>
            <Typography variant="h5">Get Email Alerts</Typography>
            <FormControl component="fieldset">
              <RadioGroup row aria-label="alert-frequency" name="alert-frequency" defaultValue="top">
                <FormControlLabel value="Daily" control={<Radio color="primary" checked={alertFreq === 'Daily'} onChange={handleFreqChange} value="Daily" name="radio-alertFreq" />} label="Daily" disabled={isAlertsDisabled} />
                <FormControlLabel value="Weekly" control={<Radio color="primary" checked={alertFreq === 'Weekly'} onChange={handleFreqChange} value="Weekly" name="radio-alertFreq" />} label="Weekly" disabled={isAlertsDisabled} />
                <FormControlLabel value="Monthly" control={<Radio color="primary" checked={alertFreq === 'Monthly'} onChange={handleFreqChange} value="Monthly" name="radio-alertFreq" />} label="Monthly" disabled={isAlertsDisabled} />
              </RadioGroup>
              {isAlertsDisabled &&
                <Typography variant="body2" component="div" className="mb-3" style={{color: '#00000060'}}>
                  Alerts are not available for {searchType(filterSearch?.propertyStatus) === 'All' ? 'All Properties' : 'Off Market'} searches. Please refine your search to buy, rent or recently sold properties.
                </Typography>
              }
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions className="justify-content-center">
          <Button onClick={saveSearch} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      <div className={styles.newFeatureAlert}>
        <NewFeatureIndicator id="savedSearch" title={getTooltipTitle(searchType(filterSearch?.propertyStatus))} content="You can now receive email notifications for your saved searches" />
      </div>
    </div>
  );
}

SavedSearchButton.propTypes = {
  searchParams: PropTypes.object,
};

export default SavedSearchButton;
